import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface Category {
  id: string;
  slug: string;
  name: string;
  sort_order: number;
  product_count?: number;
}

/**
 * Fetch categories from database with caching
 */
export const useCategories = () => {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('id, slug, name, sort_order')
        .order('sort_order');
      
      if (error) throw error;
      return data as Category[];
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

/**
 * Fetch only categories that have published products assigned to them
 */
export const useCategoriesWithProducts = () => {
  return useQuery({
    queryKey: ['categories-with-products'],
    queryFn: async () => {
      // Get all categories
      const { data: allCategories, error: categoriesError } = await supabase
        .from('categories')
        .select('id, slug, name, sort_order')
        .order('sort_order');
      
      if (categoriesError) throw categoriesError;
      if (!allCategories) return [];
      
      // Get all published products
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('categories')
        .eq('is_published', true);
      
      if (productsError) throw productsError;
      if (!products) return allCategories;
      
      // Count products per category
      const categoryCount = new Map<string, number>();
      products.forEach(product => {
        if (product.categories) {
          (product.categories as string[]).forEach(cat => {
            categoryCount.set(cat, (categoryCount.get(cat) || 0) + 1);
          });
        }
      });
      
      // Filter to only categories that have products and add count
      return allCategories
        .filter(cat => categoryCount.has(cat.slug))
        .map(cat => ({
          ...cat,
          product_count: categoryCount.get(cat.slug) || 0
        })) as Category[];
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

/**
 * Fetch only categories that have published products of a specific type
 * Uses optimized database function to count products in a single query
 * @param serviceType - 'education', 'non-education', or undefined for all
 */
export const useCategoriesWithProductsByType = (serviceType?: string) => {
  return useQuery({
    queryKey: ['categories-with-products', serviceType],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_categories_with_counts', { 
          p_service_type: serviceType || null 
        });
      
      if (error) throw error;
      return data as Category[];
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};
